import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import {
  Menu,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Chip,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  Button,
} from "@mui/material";
import { FiEdit2 } from "react-icons/fi";
import { MdFilterList, MdClear } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { SlOptionsVertical } from "react-icons/sl";

import { useAppContext } from "../contexts/AppContextProvider";
import {
  convertToIst,
  isoTimestampToReadable,
} from "../utils/timeCalculator.js";
import {
  camelCaseToCapitalCase,
  numbersOnlySanitation,
} from "../utils/inputValidations.js";
import {
  isInvalidDateRange,
  parseFilterDate,
  parsePartnerTimestamp,
} from "../utils/partnerFilterUtils.js";
import { CustomTable, EditPlatFormFee } from "./index.js";

// ─── helpers ────────────────────────────────────────────────────────────────

const epochToReadable = (epoch) => {
  if (!epoch) return "N/A";
  const num = Number(epoch);
  if (!num) return "N/A";
  const date = new Date(num * 1000);
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const DAILY_LIMIT_OPTIONS = [
  { label: "All", value: "all" },
  { label: "250", value: 250 },
  { label: "1,000", value: 1000 },
  { label: "2,000", value: 2000 },
  { label: "10,000", value: 10000 },
  { label: "1,00,000", value: 100000 },
];

const BOOL_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Yes (1)", value: 1 },
  { label: "No (0)", value: 0 },
];

const DEFAULT_FILTERS = {
  dailyMessageLimit: "all",
  isOnBizApp: "all",
  isTokenExpired: "all",
  regDateFrom: "",
  regDateTo: "",
  credDateFrom: "",
  credDateTo: "",
  tokenExpiryWithinDays: "",
};

// ─── component ───────────────────────────────────────────────────────────────

const AllUsers = () => {
  const {
    getActiveUsersByPartner,
    wabmActiveUsersByPartnerData,
    wabmUserRechargeHistory,
    wabmUserAgentsList,
    platformFeeData,

    fetchUserRechargeHistory,
    getUserAgentsList,
    getPlatformFeeData,
    migratePricingModel,
    isMigratePMLoading,
  } = useAppContext();

  const [anchorEl, setAnchorEl] = useState(null);
  const [openWabmAgentsListModal, setOpenWabmAgentsListModal] = useState(false);
  const [openRechargeHistoryModal, setOpenRechargeHistoryModal] =
    useState(false);
  const [openPlatformFeeModal, setOpenPlatformFeeModal] = useState(false);
  const [openMigrateToPM10, setOpenMigrateToPM10Model] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // ── search & filter state ──
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const showFilters = Boolean(filterAnchorEl);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [isPlatFormFeeEditOpen, setIsPlatFormFeeEditOpen] = useState(false);
  const [platFormFeeData, setPlatFormFeeData] = useState(null);
  const theme = localStorage.getItem("theme") || "dark";
  const [migrateToPM10Body, setMigrateToPM10Body] = useState({
    countryCode: "91",
    channelName: "whatsapp_api",
    coinCost: "",
  });

  // These support-only actions are intentionally unavailable in the partner UI.
  const openRegisterModal = false;
  const openWebhookModal = false;
  const wabaPin = "";
  const setWabaPin = () => {};
  const registerWabaOnSubmit = (event) => event.preventDefault();
  const handleWabaRegisterClose = () => {};
  const registerOrVerifyWebhook = () => {};
  const handleWebhookRegistrationClose = () => {};

  const openMenu = Boolean(anchorEl);

  // ─── columns ────────────────────────────────────────────────────────────────
  const getStatusChipStyles = (status) => ({
    color: status ? "var(--success-color)" : "var(--error-color)",

    borderColor: status ? "var(--success-color)" : "var(--error-color)",

    backgroundColor: status ? "var(--success-bg)" : "var(--error-bg)",
  });

  const userDataColumns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <button
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={(event) => handleClick(event, params.row)}
        >
          <SlOptionsVertical />
        </button>
      ),
    },
    { field: "id", headerName: "User ID", width: 90 },
    {
      field: "registrationDate",
      headerName: "Reg. Date",
      width: 180,
      valueGetter: (value, row) =>
        isoTimestampToReadable(convertToIst(row?.registrationDate)),
    },
    {
      field: "lastCredentialRegisterDate",
      headerName: "Last Cred. Date",
      width: 180,
      valueGetter: (value, row) =>
        row?.lastCredentialRegisterDate
          ? isoTimestampToReadable(convertToIst(row.lastCredentialRegisterDate))
          : "N/A",
    },
    { field: "corpName", headerName: "Corp. Name", width: 150 },
    { field: "firstName", headerName: "First Name", width: 150 },
    { field: "lastName", headerName: "Last Name", width: 150 },
    { field: "phoneNo", headerName: "Phone No.", width: 150 },
    { field: "corpEmail", headerName: "Reg. Email", width: 220 },
    { field: "businessName", headerName: "Business Name", width: 250 },
    { field: "businessType", headerName: "Business Type", width: 160 },
    {
      field: "wabPhoneNumber",
      headerName: "WAB Phone No.",
      width: 160,
      valueGetter: (value, row) => row?.wabPhoneNumber || "N/A",
    },
    {
      field: "wabAccountId",
      headerName: "WAB Account ID",
      width: 200,
      valueGetter: (value, row) => row?.wabAccountId || "N/A",
    },
    {
      field: "wabPhoneNumberId",
      headerName: "WAB Phone No. ID",
      width: 200,
      valueGetter: (value, row) => row?.wabPhoneNumberId || "N/A",
    },
    { field: "dailyMessageLimit", headerName: "WA Daily Limit", width: 140 },
    {
      field: "pricingModel",
      headerName: "Pricing Model",
      width: 140,
      valueGetter: (value, row) => {
        const pricingModelMap = {
          0: "Subscription",
          1: "Recharge",
          10: "Unified Recharge",
        };

        return pricingModelMap[row?.pricingModel] ?? "INVALID";
      },
    },
    {
      field: "isBlocked",
      headerName: "Blocked?",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 0 ? "No" : "Yes"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isWabaActive",
      headerName: "WABA Active?",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isOnBizApp",
      headerName: "On Biz App?",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isTokenExpired",
      headerName: "Token Expired?",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isIntlMsgAllowed",
      headerName: "Intl. Allowed?",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isMessageHistorySync",
      headerName: "Msg History Sync?",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isContactsSync",
      headerName: "Contacts Sync?",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    {
      field: "isMylinksEnabled",
      headerName: "MyLinks Enabled?",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          size="small"
          variant="outlined"
          sx={getStatusChipStyles(params.value !== 0)}
        />
      ),
    },
    { field: "mylinksProfile", headerName: "MyLinks Profile", width: 300 },
    {
      field: "wabAccessTokenExpireAt",
      headerName: "Token Expires At",
      width: 180,
      valueGetter: (value, row) => epochToReadable(row?.wabAccessTokenExpireAt),
    },
  ];

  // ─── recharge history columns ────────────────────────────────────────────────
  const rechargeHistoryColumns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "refId", headerName: "Reference ID", width: 200 },
    { field: "amount", headerName: "Amount", width: 100 },
    { field: "currency", headerName: "Currency", width: 100 },
    {
      field: "coins_purchased",
      headerName: "Coins Purchased",
      width: 150,
      valueFormatter: (value, row) =>
        row.coins_purchased?.toLocaleString() || "N/A",
    },
    {
      field: "recharge_date",
      headerName: "Recharge Date",
      width: 200,
      valueGetter: (value, row) =>
        row?.recharge_date
          ? isoTimestampToReadable(convertToIst(row.recharge_date))
          : "N/A",
    },
    {
      field: "used_payment_gateway",
      headerName: "Payment Gateway",
      width: 150,
      valueGetter: (value, row) => row?.used_payment_gateway || "N/A",
    },
  ];

  // Platform fee columns
  const platFormFeeColumns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "channel_name", headerName: "Channel Name", width: 180 },
    { field: "user_channel_id", headerName: "User Channel ID", width: 140 },
    { field: "country_code", headerName: "Country Code", width: 120 },
    { field: "country_code_iso3", headerName: "Country ISO3", width: 120 },
    { field: "entity_type", headerName: "Entity Type", width: 150 },
    { field: "coins_cost", headerName: "Coins Cost", width: 120 },
    { field: "currency", headerName: "Currency", width: 100 },
    {
      field: "price_wef",
      headerName: "Price WEF",
      width: 180,
      renderCell: (params) => <div>{params.value}</div>,
    },
    { field: "username", headerName: "Username", width: 220 },
    { field: "full_name", headerName: "Full Name", width: 180 },
    { field: "corporate_name", headerName: "Corporate ID", width: 180 },
    { field: "business_name_c", headerName: "Business Name", width: 250 },
    { field: "phone_no_c", headerName: "Phone Number", width: 150 },
    {
      field: "is_include_channel_pricing",
      headerName: "Include Channel Pricing",
      width: 200,
      renderCell: (params) => (
        <Chip
          label={params.value === 1 ? "Yes" : "No"}
          color={params.value === 1 ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    { field: "remarks", headerName: "Remarks", width: 180 },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params) => (
        <Tooltip title="Edit" arrow>
          <button
            onClick={() => {
              setPlatFormFeeData(params.row);
              setIsPlatFormFeeEditOpen(true);
            }}
            className="flex items-center justify-center text-blue-600 hover:text-blue-800"
          >
            <FiEdit2 size={18} />
          </button>
        </Tooltip>
      ),
    },
  ];

  // ─── agents list columns ─────────────────────────────────────────────────────
  const agentsListColumns = [
    { field: "agentId", headerName: "Agent ID", width: 100 },
    { field: "agentUsername", headerName: "Username", width: 150 },
    { field: "agentFullName", headerName: "Full Name", width: 200 },
    { field: "agentEmail", headerName: "Email", width: 250 },
    {
      field: "isAdmin",
      headerName: "Role",
      width: 150,
      renderCell: (params) => {
        const roleValue = params.value;
        let role = "Agent";
        let color = "default";
        if (roleValue === 2) {
          role = "Super Admin";
          color = "error";
        } else if (roleValue === 1) {
          role = "Admin";
          color = "warning";
        } else {
          role = "Agent";
          color = "info";
        }
        return (
          <Chip label={role} color={color} size="small" variant="outlined" />
        );
      },
    },
    {
      field: "isBlocked",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 0 ? "Active" : "Blocked"}
          color={params.value === 0 ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "showUnassignedChat",
      headerName: "Unassigned Chat",
      width: 150,
      valueGetter: (value, row) =>
        row?.showUnassignedChat === 1
          ? "Yes"
          : row?.showUnassignedChat === 0
            ? "No"
            : "N/A",
    },
    {
      field: "isDelete",
      headerName: "Deleted",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 0 ? "No" : "Yes"}
          color={params.value === 0 ? "success" : "warning"}
          size="small"
          variant="outlined"
        />
      ),
    },
  ];

  // ─── filtering logic ─────────────────────────────────────────────────────────
  const handleApplyFilters = () => {
    if (
      isInvalidDateRange(filters.regDateFrom, filters.regDateTo) ||
      isInvalidDateRange(filters.credDateFrom, filters.credDateTo)
    ) {
      return toast.warn(
        "The start date must be before or equal to the end date.",
      );
    }

    if (
      filters.tokenExpiryWithinDays !== "" &&
      (!Number.isInteger(Number(filters.tokenExpiryWithinDays)) ||
        Number(filters.tokenExpiryWithinDays) < 0)
    ) {
      return toast.warn(
        "Token expiry days must be a non-negative whole number.",
      );
    }

    setAppliedFilters(filters);
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);

    setAppliedFilters(DEFAULT_FILTERS);
    setFilterAnchorEl(null);
  };

  const filteredData = useMemo(() => {
    if (!wabmActiveUsersByPartnerData) return [];

    return wabmActiveUsersByPartnerData.filter((row) => {
      // ── dailyMessageLimit ──
      if (
        appliedFilters.dailyMessageLimit !== "all" &&
        Number(row.dailyMessageLimit) !==
          Number(appliedFilters.dailyMessageLimit)
      )
        return false;

      // ── isOnBizApp ──
      if (
        appliedFilters.isOnBizApp !== "all" &&
        Number(row.isOnBizApp) !== Number(appliedFilters.isOnBizApp)
      )
        return false;

      // ── isTokenExpired ──
      if (
        appliedFilters.isTokenExpired !== "all" &&
        Number(row.isTokenExpired) !== Number(appliedFilters.isTokenExpired)
      )
        return false;

      // ── registrationDate range ──
      if (appliedFilters.regDateFrom || appliedFilters.regDateTo) {
        const regDate = parseFilterDate(row.registrationDate);
        if (!regDate) return false;
        if (
          appliedFilters.regDateFrom &&
          regDate < parseFilterDate(appliedFilters.regDateFrom)
        )
          return false;
        if (appliedFilters.regDateTo) {
          const toDate = parseFilterDate(appliedFilters.regDateTo, true);
          if (regDate > toDate) return false;
        }
      }

      // ── lastCredentialRegisterDate range ──
      if (appliedFilters.credDateFrom || appliedFilters.credDateTo) {
        const credDate = parseFilterDate(row.lastCredentialRegisterDate);
        if (!credDate) return false;
        if (
          appliedFilters.credDateFrom &&
          credDate < parseFilterDate(appliedFilters.credDateFrom)
        )
          return false;
        if (appliedFilters.credDateTo) {
          const toDate = parseFilterDate(appliedFilters.credDateTo, true);
          if (credDate > toDate) return false;
        }
      }

      // ── Token expires within X days ──
      if (appliedFilters.tokenExpiryWithinDays) {
        const expiryDate = parsePartnerTimestamp(row.wabAccessTokenExpireAt);

        // Invalid or missing date
        if (isNaN(expiryDate.getTime())) {
          return false;
        }

        const now = new Date();
        const days = Number(appliedFilters.tokenExpiryWithinDays);

        const endDate = new Date(now);
        endDate.setDate(endDate.getDate() + days);

        // Keep only tokens expiring between now and endDate
        if (expiryDate < now || expiryDate > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [wabmActiveUsersByPartnerData, appliedFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.dailyMessageLimit !== "all") count++;
    if (appliedFilters.isOnBizApp !== "all") count++;
    if (appliedFilters.isTokenExpired !== "all") count++;
    if (appliedFilters.regDateFrom || appliedFilters.regDateTo) count++;
    if (appliedFilters.credDateFrom || appliedFilters.credDateTo) count++;
    if (appliedFilters.tokenExpiryWithinDays !== "") count++;
    return count;
  }, [appliedFilters]);

  // ─── handlers ────────────────────────────────────────────────────────────────

  const handleClick = (event, rowData) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(rowData);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleWabmAgentsListClose = () => {
    setOpenWabmAgentsListModal(false);
    setSelectedUser(null);
  };

  const handleUserRechargeHistoryClose = () => {
    setOpenRechargeHistoryModal(false);
    setSelectedUser(null);
  };

  const handlePlatformFeeClose = () => {
    setOpenPlatformFeeModal(false);
    setSelectedUser(null);
  };

  const handleMigrateToPM10Close = () => {
    setOpenMigrateToPM10Model(false);
  };

  const handleActionOnClick = (action) => {
    if (!selectedUser) return;

    switch (action) {
      case "wabmUserAgentsList":
        setOpenWabmAgentsListModal(true);
        break;
      case "getUserRechargeHistory":
        setOpenRechargeHistoryModal(true);
        break;
      case "getPlatformFee":
        setOpenPlatformFeeModal(true);
        break;
      case "migrateToPM10":
        setOpenMigrateToPM10Model(true);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  // ─── recharge / agent helpers ─────────────────────────────────────────────────

  const formatNumber = (num) => num?.toLocaleString() || "0";

  const getRemainingCoins = (coinTracker) => {
    if (!coinTracker) return "N/A";
    return formatNumber(
      coinTracker?.total_coins_purchased -
        coinTracker?.total_coins_spent -
        coinTracker?.num_of_coins_expired,
    );
  };

  const getAgentStats = (agents) => {
    if (!agents || agents.length === 0) return null;
    return {
      total: agents.length,
      active: agents.filter((a) => a.isDelete === 0).length,
      blocked: agents.filter((a) => a.isBlocked === 1).length,
      admins: agents.filter((a) => a.isAdmin === 1).length,
      superAdmins: agents.filter((a) => a.isAdmin === 2).length,
      regularAgents: agents.filter((a) => a.isAdmin === 0).length,
    };
  };

  const handleMigrateToPM10OnSubmit = () => {
    if (migrateToPM10Body.coinCost == "") {
      return toast.warn("Coins cost  is required");
    }
    if (Number(migrateToPM10Body.coinCost) < 5) {
      return toast.warn("Min coins cost 5 is allowed");
    }
    const payload = {
      user_id: selectedUser?.id,
      country_code: migrateToPM10Body.countryCode,
      channel_name: migrateToPM10Body.channelName,
      coins_cost: Number(migrateToPM10Body.coinCost),
    };
    migratePricingModel(payload);
    handleMigrateToPM10Close();
  };

  useEffect(() => {
    getActiveUsersByPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── render ──────────────────────────────────────────────────────────────────

  return (
    <main className="p-5 space-y-5">
      <div className="space-y-1">
        <h2>All Users</h2>
        <p>Manage your registered and approved WaBM users.</p>
      </div>

      {/* ── Search & Filter Bar ── */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-white">
          {/* Toggle filters */}
          <Tooltip title="Toggle filters">
            <button
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              className={`flex items-center gap-1 bg-accent px-3 py-1.5 rounded border border-borderColor text-sm font-medium transition-colors `}
            >
              <MdFilterList size={18} />
              Filters{" "}
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-white text-accent text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </Tooltip>

          {/* Reset */}
          {activeFilterCount > 0 && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 px-3 py-1.5 rounded border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors"
            >
              <MdClear size={16} />
              Reset all
            </button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (value === "all" || value === "" || value === null)
                return null;

              return (
                <Chip
                  key={key}
                  label={`${camelCaseToCapitalCase(key).replace(
                    /^Is\s/,
                    "",
                  )}: ${value}`}
                  onDelete={() => {
                    const updatedFilters = {
                      ...appliedFilters,
                      [key]: DEFAULT_FILTERS[key],
                    };

                    setAppliedFilters(updatedFilters);

                    setFilters((prev) => ({
                      ...prev,
                      [key]: DEFAULT_FILTERS[key],
                    }));
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "var(--border-color)",
                    color: "var(--text-secondary)",

                    "& .MuiChip-deleteIcon": {
                      color: "var(--text-primary)",
                    },

                    "& .MuiChip-deleteIcon:hover": {
                      color: "var(--text-primary)",
                      opacity: 0.8,
                    },
                  }}
                />
              );
            })}
          </div>

          {/* Result count */}
          <span className="text-sm text-gray-500 ml-auto">
            Showing <strong>{filteredData.length}</strong> of{" "}
            <strong>{wabmActiveUsersByPartnerData?.length ?? 0}</strong> users
          </span>
        </div>
      </div>

      <CustomTable
        rows={filteredData}
        columns={userDataColumns}
        pageSize={50}
        pageSizeOptions={[50, 100]}
        searchable={true}
        searchPlaceholder="Search…"
        emptyMessage="No users found."
        isExport={true}
      />

      {/* ── Context Menu ── */}
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: "var(--background-primary)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-color)",
            "& .MuiMenuItem-root": {
              fontSize: "13px",
            },
          },
        }}
      >
        <MenuItem
          sx={{
            background: "var(--background-primary)",
            "&:hover": { background: "var(--background-secondary)" },
            "&.Mui-selected": {
              background: "var(--background-secondary)",
            },
            "&.Mui-selected:hover": {
              background: "var(--background-secondary)",
            },
          }}
          onClick={() => {
            handleActionOnClick("wabmUserAgentsList");
            getUserAgentsList(selectedUser?.id);
          }}
        >
          List of Agents
        </MenuItem>
        <MenuItem
          sx={{
            background: "var(--background-primary)",
            "&:hover": { background: "var(--background-secondary)" },
            "&.Mui-selected": {
              background: "var(--background-secondary)",
            },
            "&.Mui-selected:hover": {
              background: "var(--background-secondary)",
            },
          }}
          onClick={() => {
            handleActionOnClick("getUserRechargeHistory");
            fetchUserRechargeHistory(selectedUser?.id);
          }}
        >
          Recharge History
        </MenuItem>
        <MenuItem
          disabled={selectedUser?.pricingModel !== 10}
          sx={{
            background: "var(--background-primary)",
            "&:hover": { background: "var(--background-secondary)" },
            "&.Mui-selected": {
              background: "var(--background-secondary)",
            },
            "&.Mui-selected:hover": {
              background: "var(--background-secondary)",
            },
          }}
          onClick={() => {
            handleActionOnClick("getPlatformFee");
            getPlatformFeeData(selectedUser?.id);
          }}
        >
          Platform Fee
        </MenuItem>
        <MenuItem
          disabled={selectedUser?.pricingModel == 10}
          sx={{
            background: "var(--background-primary)",
            "&:hover": { background: "var(--background-secondary)" },
            "&.Mui-selected": {
              background: "var(--background-secondary)",
            },
            "&.Mui-selected:hover": {
              background: "var(--background-secondary)",
            },
          }}
          onClick={() => {
            handleActionOnClick("migrateToPM10");
          }}
        >
          Migrate to PM10
        </MenuItem>
      </Menu>

      {/* ── Register WAB API Modal ── */}
      {false && (
        <>
          <Modal open={openRegisterModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "var(--background-primary)",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                width: "80%",
                maxWidth: 600,
              }}
              className="text-textPrimary space-y-3"
            >
              <h3 className="mb-2">Register WAB API</h3>
              <div>
                <p>
                  Want to confirm registration/re-registration for{" "}
                  <strong>
                    {selectedUser?.firstName} {selectedUser?.lastName}
                  </strong>
                  ?
                </p>
                <p>
                  Phone Number: <strong>+{selectedUser?.phoneNo}</strong>
                </p>
                <form
                  onSubmit={registerWabaOnSubmit}
                  className="flex flex-col gap-2 mt-3"
                >
                  <input
                    type="text"
                    placeholder="Enter 6 digit PIN"
                    value={wabaPin}
                    required
                    onChange={(e) =>
                      setWabaPin(numbersOnlySanitation(e.target.value))
                    }
                    className="my-3"
                  />
                  <div className="flex justify-between gap-2">
                    <button type="submit" className="button-1 w-fit">
                      Register
                    </button>
                    <button
                      type="button"
                      onClick={handleWabaRegisterClose}
                      className="button-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </Box>
          </Modal>

          {/* ── Webhook Modal ── */}
          <Modal open={openWebhookModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "var(--background-primary)",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                width: "80%",
                maxWidth: 600,
              }}
              className="text-textPrimary space-y-3"
            >
              <h3 className="mb-2">WAB API Webhook</h3>
              <div className="space-y-1.5">
                <p>
                  Attach or Verify Webhook for{" "}
                  <strong>
                    {selectedUser?.firstName} {selectedUser?.lastName}
                  </strong>
                  ?
                </p>
                <p>
                  Corp ID: <strong>{selectedUser?.corpName}</strong>
                </p>
                <p>
                  WABA Phone Number:{" "}
                  <strong>+{selectedUser?.wabPhoneNumber}</strong>
                </p>
                <p>
                  WABA ID: <strong>{selectedUser?.wabAccountId}</strong>
                </p>
              </div>
              <div className="mt-2 flex justify-between gap-2">
                <button
                  type="button"
                  onClick={() =>
                    registerOrVerifyWebhook(selectedUser?.id, "POST")
                  }
                  className="button-1"
                >
                  Attach Webhook
                </button>
                <button
                  type="button"
                  onClick={() =>
                    registerOrVerifyWebhook(selectedUser?.id, "GET")
                  }
                  className="button-1"
                >
                  Verify Webhook
                </button>
                <button
                  type="button"
                  onClick={handleWebhookRegistrationClose}
                  className="button-cancel"
                >
                  Cancel
                </button>
              </div>
            </Box>
          </Modal>
        </>
      )}

      {/* ── Agents List Modal ── */}
      <Modal open={openWabmAgentsListModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--background-primary)",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "90%",
            maxWidth: 1200,
            maxHeight: "90vh",
            overflow: "auto",
          }}
          className="relative text-textPrimary space-y-4"
        >
          <div className="flex gap-6 justify-between ">
            <Typography
              variant="h5"
              component="h4"
              className="text-themeColor1"
            >
              Agents List
            </Typography>
            <div className="flex  gap-3 items-center">
              <p>
                User:{" "}
                <span className="bg-accent rounded-md px-2 py-1 text-sm text-white">
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </span>
              </p>
              <p>
                Corp ID:{" "}
                <span className="bg-accent rounded-md px-2 py-1 text-sm text-white">
                  {selectedUser?.corpName}
                </span>{" "}
              </p>
            </div>
            <div>
              {" "}
              <button
                onClick={handleWabmAgentsListClose}
                className="button-cancel  py-2 px-2.5"
              >
                <RxCross2 size={18} />
              </button>
            </div>
          </div>

          {wabmUserAgentsList ? (
            <div className="space-y-4">
              {(() => {
                const stats = getAgentStats(wabmUserAgentsList);
                return stats ? (
                  <Card
                    elevation={1}
                    sx={{
                      backgroundColor: "var(--background-secondary)",
                      color: "var(--text-secondary)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ color: "#56CCF2" }}
                      >
                        Agents Summary
                      </Typography>
                      <Grid container spacing={3}>
                        {[
                          {
                            label: "Total Agents",
                            value: stats.total,
                            color: "#2F80ED",
                          },
                          {
                            label: "Active Agents",
                            value: stats.active,
                            color: "#25D366",
                          },
                          {
                            label: "Blocked Agents",
                            value: stats.blocked,
                            color: "#ef4444",
                          },
                          {
                            label: "Super Admins",
                            value: stats.superAdmins,
                            color: "#9333ea",
                          },
                          {
                            label: "Admins",
                            value: stats.admins,
                            color: "#f59e0b",
                          },
                          {
                            label: "Agents",
                            value: stats.regularAgents,
                            color: "#06b6d4",
                          },
                        ].map(({ label, value, color }) => (
                          <Grid item xs={12} sm={6} md={2} key={label}>
                            <Box>
                              <Typography variant="subtitle2">
                                {label}
                              </Typography>
                              <Typography variant="h6" sx={{ color }}>
                                {value}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              <Card
                elevation={1}
                sx={{
                  backgroundColor: "var(--background-secondary)",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Agents Details
                  </Typography>
                  <CustomTable
                    rows={wabmUserAgentsList}
                    columns={agentsListColumns}
                    pageSize={50}
                    pageSizeOptions={[10, 25, 50]}
                    searchable={true}
                    searchPlaceholder="Search…"
                    emptyMessage="No agents found for this user."
                    height="50vh"
                  />
                </CardContent>
              </Card>
            </div>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                Loading agents list...
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {/* ── Recharge History Modal ── */}
      <Modal open={openRechargeHistoryModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--background-primary)",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "90%",
            maxWidth: 1200,
            maxHeight: "90vh",
            overflow: "auto",
          }}
          className="relative text-textPrimary space-y-5"
        >
          <div className="flex gap-6 justify-between items-center ">
            <Typography
              variant="h5"
              component="h4"
              className="text-themeColor1"
            >
              Recharge History
            </Typography>
            <div className="flex  gap-3 items-center">
              <p>
                Name :{" "}
                <span className="bg-accent rounded-md px-2 py-1 text-sm text-white">
                  {selectedUser?.firstName} {selectedUser?.lastName}
                </span>{" "}
              </p>
              <p>
                Corp ID :{" "}
                <span className="bg-accent rounded-md px-2 py-1 text-sm text-white">
                  {selectedUser?.corpName}
                </span>{" "}
              </p>
            </div>
            <div>
              {" "}
              <button
                onClick={handleUserRechargeHistoryClose}
                className="button-cancel  py-2 px-2.5"
              >
                <RxCross2 size={18} />
              </button>
            </div>
          </div>

          {wabmUserRechargeHistory ? (
            <div className="space-y-4">
              <Card
                elevation={1}
                sx={{
                  bgcolor: "var(--background-secondary)",
                }}
              >
                <CardContent>
                  <h3 className="text-textPrimary mb-2 ">
                    Coin Tracker Summary
                  </h3>
                  <Grid container spacing={3}>
                    {[
                      {
                        label: "Total Coins Purchased",
                        value: formatNumber(
                          wabmUserRechargeHistory.coin_tracker
                            ?.total_coins_purchased,
                        ),
                        color: "var(--text-primary)",
                      },
                      {
                        label: "Total Coins Spent",
                        value: formatNumber(
                          wabmUserRechargeHistory.coin_tracker
                            ?.total_coins_spent,
                        ),
                        color: "#ef4444",
                      },
                      {
                        label: "Coins Expired",
                        value:
                          wabmUserRechargeHistory.coin_tracker
                            ?.num_of_coins_expired,
                        color: "#2F80ED",
                      },
                      {
                        label: "Remaining Coins",
                        value: getRemainingCoins(
                          wabmUserRechargeHistory.coin_tracker,
                        ),
                        color: "#2F80ED",
                      },
                      {
                        label: "Last Purchase Amount",
                        value: formatNumber(
                          wabmUserRechargeHistory.coin_tracker
                            ?.num_of_coins_last_purchased,
                        ),
                        color: "#FFD200",
                      },
                    ].map(({ label, value, color }) => (
                      <Grid item xs={12} sm={6} md={3} key={label}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "var(--text-secondary)" }}
                          >
                            {label}
                          </Typography>
                          <Typography variant="h6" sx={{ color }}>
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "var(--text-secondary)" }}
                        >
                          Coins per Incoming Conversation
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "var(--text-primary)" }}
                        >
                          {wabmUserRechargeHistory.coin_tracker
                            ?.coins_deduct_per_incoming_conv || 0}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "var(--text-secondary)" }}
                        >
                          Coins per Outgoing Conversation
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "var(--text-primary)" }}
                        >
                          {wabmUserRechargeHistory.coin_tracker
                            ?.coins_deduct_per_outgoing_conv || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card
                elevation={1}
                sx={{
                  bgcolor: "var(--background-secondary)",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Transaction History
                  </Typography>
                  <CustomTable
                    rows={wabmUserRechargeHistory.recharge_history}
                    columns={rechargeHistoryColumns}
                    pageSize={10}
                    pageSizeOptions={[10, 25, 50]}
                    searchable={true}
                    searchPlaceholder="Search…"
                    emptyMessage="No recharge history found."
                  />
                </CardContent>
              </Card>
            </div>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                Loading recharge history...
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal open={openPlatformFeeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--background-primary)",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "90%",
            maxWidth: 1200,
            maxHeight: "90vh",
            overflow: "auto",
          }}
          className="relative text-textPrimary space-y-5"
        >
          <div className="flex gap-6 justify-between items-center ">
            <Typography
              variant="h5"
              component="h4"
              className="text-themeColor1"
            >
              Platform Fee
            </Typography>

            <div>
              <button
                onClick={handlePlatformFeeClose}
                className="button-cancel  py-2 px-2.5"
              >
                <RxCross2 size={18} />
              </button>
            </div>
          </div>

          {platformFeeData ? (
            <div className="space-y-4">
              <CustomTable
                rows={platformFeeData}
                columns={platFormFeeColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50]}
                searchable={true}
                searchPlaceholder="Search…"
                emptyMessage="No platform fee found."
                freezeLastColumn={true}
              />
            </div>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                Loading platform fee...
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal open={openMigrateToPM10}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "var(--background-primary)",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "90%",
            maxWidth: 600,
            maxHeight: "90vh",
            overflow: "auto",
          }}
          className="relative text-textPrimary space-y-5"
        >
          <div className="flex flex-col gap-4 w-full">
            <h3 className="text-xl text-center">Migrate to PM10</h3>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": { color: "var(--text-primary)" },
                }}
              >
                Country Code
              </InputLabel>

              <Select
                label="Country Code"
                value={migrateToPM10Body.countryCode}
                onChange={(e) =>
                  setMigrateToPM10Body((f) => ({
                    ...f,
                    countryCode: e.target.value,
                  }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                <MenuItem
                  value="91"
                  sx={{
                    background: "var(--background-primary)",
                    "&:hover": { background: "var(--background-secondary)" },
                    "&.Mui-selected": {
                      background: "var(--background-secondary)",
                    },
                    "&.Mui-selected:hover": {
                      background: "var(--background-secondary)",
                    },
                  }}
                >
                  +91{" "}
                </MenuItem>
                <MenuItem
                  value="1"
                  sx={{
                    background: "var(--background-primary)",
                    "&:hover": { background: "var(--background-secondary)" },
                    "&.Mui-selected": {
                      background: "var(--background-secondary)",
                    },
                    "&.Mui-selected:hover": {
                      background: "var(--background-secondary)",
                    },
                  }}
                >
                  +1
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": { color: "var(--text-primary)" },
                }}
              >
                Channel Name
              </InputLabel>

              <Select
                label="Channel Name"
                value={migrateToPM10Body.channelName}
                onChange={(e) =>
                  setMigrateToPM10Body((f) => ({
                    ...f,
                    channelName: e.target.value,
                  }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                <MenuItem
                  value="whatsapp_api"
                  sx={{
                    background: "var(--background-primary)",
                    "&:hover": { background: "var(--background-secondary)" },
                    "&.Mui-selected": {
                      background: "var(--background-secondary)",
                    },
                    "&.Mui-selected:hover": {
                      background: "var(--background-secondary)",
                    },
                  }}
                >
                  WhatsApp API
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Coin Cost"
              type="number"
              inputProps={{ min: 5 }}
              value={migrateToPM10Body.coinCost}
              onChange={(e) =>
                setMigrateToPM10Body((f) => ({
                  ...f,
                  coinCost: e.target.value,
                }))
              }
              sx={{
                "& .MuiInputLabel-root": {
                  color: "var(--text-primary)",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "var(--text-primary)",
                },
                "& .MuiOutlinedInput-root": {
                  color: "var(--text-primary)",
                  "& fieldset": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused": {
                    color: "var(--text-primary)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--text-primary)",
                  },
                  "& input": {
                    color: "var(--text-primary)",
                  },
                  "& input:focus": {
                    color: "var(--text-primary)",
                  },
                },
              }}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
            }}
          >
            <Button variant="outlined" onClick={handleMigrateToPM10Close}>
              Cancel
            </Button>

            <Button variant="contained" onClick={handleMigrateToPM10OnSubmit}>
              {isMigratePMLoading ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Popup filter panel ── */}
      <Dialog
        open={showFilters}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            background: "var(--background-primary)",
            borderRadius: 2,
            maxWidth: "600px",
            width: "100%",
          },
        }}
      >
        <div className="p-6 bg-backgroundPrimary text-textPrimary rounded-lg  ">
          <h3 className="text-center mb-4">Filters</h3>
          <div className="grid grid-cols-1 gap-4">
            {/* Daily Message Limit */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": { color: "var(--text-primary)" },
                }}
              >
                Daily Msg Limit
              </InputLabel>
              <Select
                label="Daily Msg Limit"
                value={filters.dailyMessageLimit}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    dailyMessageLimit: e.target.value,
                  }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                {DAILY_LIMIT_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      background: "var(--background-primary)",
                      "&:hover": { background: "var(--background-secondary)" },
                      "&.Mui-selected": {
                        background: "var(--background-secondary)",
                      },
                      "&.Mui-selected:hover": {
                        background: "var(--background-secondary)",
                      },
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* isOnBizApp */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": { color: "var(--text-primary)" },
                }}
              >
                On Biz App?
              </InputLabel>
              <Select
                label="On Biz App?"
                value={filters.isOnBizApp}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, isOnBizApp: e.target.value }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                {BOOL_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      background: "var(--background-primary)",
                      "&:hover": { background: "var(--background-secondary)" },
                      "&.Mui-selected": {
                        background: "var(--background-secondary)",
                      },
                      "&.Mui-selected:hover": {
                        background: "var(--background-secondary)",
                      },
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* isTokenExpired */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel
                sx={{
                  color: "var(--text-primary)",
                  "&.Mui-focused": { color: "var(--text-primary)" },
                }}
              >
                Token Expired?
              </InputLabel>
              <Select
                label="Token Expired?"
                value={filters.isTokenExpired}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, isTokenExpired: e.target.value }))
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: "var(--background-primary)",
                      color: "var(--text-primary)",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--text-primary)",
                  },
                  "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                  "& .MuiSelect-select": {
                    color: "var(--text-primary)",
                  },
                }}
              >
                {BOOL_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      background: "var(--background-primary)",
                      "&:hover": { background: "var(--background-secondary)" },
                      "&.Mui-selected": {
                        background: "var(--background-secondary)",
                      },
                      "&.Mui-selected:hover": {
                        background: "var(--background-secondary)",
                      },
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="number"
              label="Token Expiry Within Days"
              inputProps={{ min: 0, step: 1 }}
              value={filters.tokenExpiryWithinDays}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  tokenExpiryWithinDays: e.target.value,
                }))
              }
              fullWidth
              sx={{
                "& .MuiInputLabel-root": {
                  color: "var(--text-primary)",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "var(--text-primary)",
                },
                "& .MuiOutlinedInput-root": {
                  color: "var(--text-primary)",
                  "& fieldset": {
                    borderColor: "var(--border-color)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--border-color)",
                  },
                  "&.Mui-focused": {
                    color: "var(--text-primary)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--text-primary)",
                  },
                  "& input": {
                    color: "var(--text-primary)",
                  },
                  "& input:focus": {
                    color: "var(--text-primary)",
                  },
                },
              }}
            />

            {/* Registration Date range */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-textSecondary font-medium mb-1.5">
                Registration Date
              </span>
              <div className="flex gap-2 items-center">
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={filters.regDateFrom}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, regDateFrom: e.target.value }))
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--text-primary)",
                      "& fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--text-primary)",
                      },
                      "& input": {
                        color: "var(--text-primary)",
                      },
                      "& input:focus": {
                        color: "var(--text-primary)",
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: theme === "dark" ? "invert(1)" : "invert(0)",
                        cursor: "pointer",
                      },
                    },
                  }}
                />
                <span className="text-gray-400">–</span>
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={filters.regDateTo}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, regDateTo: e.target.value }))
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--text-primary)",
                      "& fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--text-primary)",
                      },
                      "& input": {
                        color: "var(--text-primary)",
                      },
                      "& input:focus": {
                        color: "var(--text-primary)",
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: theme === "dark" ? "invert(1)" : "invert(0)",
                        cursor: "pointer",
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Last Credential Register Date range */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-textSecondary font-medium mb-1.5">
                Last Cred. Register Date
              </span>
              <div className="flex gap-2 items-center">
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={filters.credDateFrom}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, credDateFrom: e.target.value }))
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--text-primary)",
                      "& fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--text-primary)",
                      },
                      "& input": {
                        color: "var(--text-primary)",
                      },
                      "& input:focus": {
                        color: "var(--text-primary)",
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: theme === "dark" ? "invert(1)" : "invert(0)",
                        cursor: "pointer",
                      },
                    },
                  }}
                />
                <span className="text-gray-400">–</span>
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={filters.credDateTo}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, credDateTo: e.target.value }))
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputLabel-root": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--text-primary)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--text-primary)",
                      "& fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--text-primary)",
                      },
                      "& input": {
                        color: "var(--text-primary)",
                      },
                      "& input:focus": {
                        color: "var(--text-primary)",
                      },
                      "& input::-webkit-calendar-picker-indicator": {
                        filter: theme === "dark" ? "invert(1)" : "invert(0)",
                        cursor: "pointer",
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-4">
              <Button
                variant="outlined"
                color="error"
                onClick={handleResetFilters}
              >
                Reset
              </Button>

              <Button variant="contained" onClick={handleApplyFilters}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Edit platformfee modal  */}
      <Dialog
        open={isPlatFormFeeEditOpen}
        PaperProps={{
          sx: {
            background: "var(--background-primary)",
            borderRadius: 2,
            maxWidth: "640px",
            width: "100%",
          },
        }}
      >
        <EditPlatFormFee
          platFormFeeData={platFormFeeData}
          onClose={() => setIsPlatFormFeeEditOpen(false)}
        />
      </Dialog>
    </main>
  );
};

export default AllUsers;
